import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { faUser } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@morressier/alembic';
import { useState } from 'react';
import { TagDetails } from 'src/components/Submission/AuthorsTab/AuthorProfileModal/TagDetailsPopover';

export interface MergeUserTagSummary {
  description?: string;
  color?: string;
  name: string;
  tagCategory?: string;
  tagDescription?: string;
  usage: 'users' | 'submissions';
}

interface UserTagBadgeListProps {
  tags: MergeUserTagSummary[];
  keyPrefix?: string;
  maxVisible?: number;
}

const UserTagBadgeList = ({
  tags,
  keyPrefix = 'user-tag',
  maxVisible
}: UserTagBadgeListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!tags.length) {
    return <Text textStyle="textBody">-</Text>;
  }

  const hasOverflow = typeof maxVisible === 'number' && tags.length > maxVisible;
  const visibleTags =
    hasOverflow && !isExpanded ? tags.slice(0, maxVisible) : tags;

  return (
    <Flex gap={2} wrap="wrap">
      {visibleTags.map(tag => (
        <Tooltip
          hasArrow
          key={`${keyPrefix}-${tag.name}`}
          borderRadius={4}
          placement="auto"
          background="surfacePrimaryFill"
          color="neutralSecondaryContent"
          label={
            <TagDetails
              tags={{
                name: tag.name,
                description: tag.description ?? '',
                tagCategory: tag.tagCategory ?? '',
                tagColor: tag.color ?? '',
                tagDescription: tag.tagDescription ?? '',
                usage: tag.usage
              }}
            />
          }
        >
          <Box width="fit-content" maxWidth="100%" minWidth={0}>
            <Badge size="context" variant="neutral" maxWidth="100%">
              <Flex
                gap={1}
                alignItems="center"
                color="neutralSecondaryContent"
                minWidth={0}
                maxWidth="100%"
              >
                <FontAwesomeIcon icon={faUser} color={tag.color} />
                <Text
                  textStyle="textContext"
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  minWidth={0}
                >
                  {tag.name}
                </Text>
              </Flex>
            </Badge>
          </Box>
        </Tooltip>
      ))}
      {hasOverflow && isExpanded ? (
        <Badge
          size="context"
          variant="neutral"
          cursor="pointer"
          onClick={() => setIsExpanded(false)}
        >
          <Text textStyle="textContext">Show less</Text>
        </Badge>
      ) : null}
      {hasOverflow && !isExpanded ? (
        <Badge
          size="context"
          variant="neutral"
          cursor="pointer"
          onClick={() => setIsExpanded(true)}
        >
          <Text textStyle="textContext">+{tags.length - maxVisible}</Text>
        </Badge>
      ) : null}
    </Flex>
  );
};

export default UserTagBadgeList;
